import { KPI } from '@/components/ui/KPI';
import { SectionHead } from '@/components/ui/SectionHead';
import { ChartCard } from '@/components/ui/ChartCard';
import { TopPrizesList } from '@/components/TopPrizesList';
import { CouponsByBrandTable } from '@/components/CouponsByBrandTable';
import { CouponStockTable } from '@/components/CouponStockTable';
import { StackedDailyBars } from '@/components/StackedDailyBars';
import { SeriesBars } from '@/components/SeriesBars';
import type { RewardsCouponsSection } from '@/api/worldCup';
import type { DashboardConfig } from '../config';

type Props = {
  section: RewardsCouponsSection;
  brandColors: Record<string, string>;
  hide?: DashboardConfig['hide'];
};

export function RewardsCouponsView({ section, brandColors, hide = {} }: Props) {
  const { kpis } = section;
  // Headline KPI grid — every tile beyond rewards_awarded is payload-driven:
  // coupons_remaining is absent where no inventory number exists (50/3), the
  // email/in-store split only ships where redemption tracking is wired.
  const tileCount =
    1 + (kpis.rewards_claimed ? 1 : 0) + (kpis.coupons_remaining ? 1 : 0) + (kpis.coupons_used_email ? 1 : 0) + (kpis.coupons_used_in_store ? 1 : 0);
  const headlineGrid = `grid-${Math.min(4, Math.max(2, tileCount))}`;

  return (
    <div>
      {/* Heading and tile labels are payload-driven: the reward noun differs per
          campaign ("Menu distribution" / "Menus awarded" for Coca-Cola Quick). */}
      <SectionHead tag={section.tag ?? 'rewards & coupons'}>{section.title ?? 'Reward distribution'}</SectionHead>
      <div className={headlineGrid}>
        <KPI label={kpis.rewards_awarded.label ?? 'Rewards awarded'} value={kpis.rewards_awarded.display} accent="engage" foot={kpis.rewards_awarded.foot} />
        {kpis.rewards_claimed && (
          <KPI label={kpis.rewards_claimed.label ?? 'Rewards claimed'} value={kpis.rewards_claimed.display} accent="engage" foot={kpis.rewards_claimed.foot} />
        )}
        {kpis.coupons_remaining && (
          <KPI
            label={kpis.coupons_remaining.label ?? 'Coupons remaining'}
            value={kpis.coupons_remaining.display}
            accent="engage"
            foot={kpis.coupons_remaining.foot}
          />
        )}
        {kpis.coupons_used_email && (
          <KPI label="Coupons used (email)" value={kpis.coupons_used_email.display} accent="engage" foot={kpis.coupons_used_email.foot} />
        )}
        {kpis.coupons_used_in_store && (
          <KPI label="Coupons used (in store)" value={kpis.coupons_used_in_store.display} accent="engage" foot={kpis.coupons_used_in_store.foot} />
        )}
      </div>

      {section.coupons_by_brand && section.coupons_by_brand.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <CouponsByBrandTable rows={section.coupons_by_brand} brandColors={brandColors} />
        </div>
      )}

      {/* Remaining coupon inventory per brand (barcode AVAILABLE vs ISSUED).
          Brazil-only today; hidden cleanly where the pipeline doesn't ship it. */}
      {section.coupon_stock && section.coupon_stock.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <CouponStockTable rows={section.coupon_stock} brandColors={brandColors} />
        </div>
      )}

      {/* Rewards per day, campaign-wide (no brand split) — campaigns with
          sponsor brands ship coupons_by_day instead.

          `rewards_by_day_stack` wins when present: it carries several series
          (awarded vs claimed) for one chart. They are drawn SIDE BY SIDE, not
          stacked — a claimed reward was also awarded, so a stack would show a
          total that doesn't exist. */}
      {section.rewards_by_day_stack && section.rewards_by_day_stack.series.length > 0 ? (
        <div style={{ marginTop: 18 }}>
          <ChartCard title={section.rewards_by_day_stack.label} unit={section.rewards_by_day_stack.unit}>
            <StackedDailyBars
              data={section.rewards_by_day_stack}
              grouped
              keys={section.rewards_by_day_stack.brands.map((b, i) => ({
                key: b.brand_id,
                label: b.name,
                color: i === 0 ? 'var(--accent-muted)' : 'var(--accent)',
              }))}
            />
          </ChartCard>
        </div>
      ) : (
        section.rewards_by_day &&
        section.rewards_by_day.series.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <SeriesBars daily={section.rewards_by_day} color="var(--accent-muted)" />
          </div>
        )
      )}

      {/* Coupons distributed per day, stacked by brand (Spain). */}
      {section.coupons_by_day && section.coupons_by_day.brands.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <ChartCard title={section.coupons_by_day.label} unit={section.coupons_by_day.unit}>
            <StackedDailyBars data={section.coupons_by_day} keys={section.coupons_by_day.brands.map((b) => ({ key: b.brand_id, label: b.name }))} />
          </ChartCard>
        </div>
      )}

      {/* Top prizes — hidden for BR + France (coupons-by-brand covers the
          per-brand picture more usefully there). Spain keeps it. */}
      {!hide.topPrizes && (
        <div style={{ marginTop: 18 }}>
          <TopPrizesList prizes={section.top_prizes} brandColors={brandColors} />
        </div>
      )}
    </div>
  );
}
